import type { AssistantTool } from '@rammetec/app-kit/assistant'
import type { ModuleInstance } from '@syscolect/domain'
import type { AppModule } from '@/app/modules/registry'
import { createAssistantDataTools } from './assistantDataTools'
import type { AssistantCreateToolsDeps } from './assistantCreateRecord'
import {
  queryableCatalogKey,
  type AssistantQueryableEntity,
} from './assistantEntityCatalog'
import {
  createMemoryRememberTool,
  type AssistantMemoryToolsDeps,
} from './assistantMemoryTool'

export interface AssistantLocationSnapshot {
  pathname: string
  mid: string | null
  modules: readonly AppModule[]
  instances: readonly ModuleInstance[]
}

/** One leaf module the assistant may open — `label` is unique in the enum. */
export interface OpenableModuleOption {
  label: string
  instanceId: string
  slug: string
}

function instanceDisplayLabel(
  inst: ModuleInstance,
  modBySlug: ReadonlyMap<string, AppModule>,
): string {
  return inst.label ?? modBySlug.get(inst.slug)?.label ?? inst.slug
}

/**
 * Leaf instances only (groups have no module scope). Duplicate sidebar labels
 * are disambiguated with the parent group label: `Pedidos (Comercial)`.
 */
export function buildOpenableModuleOptions(
  instances: readonly ModuleInstance[],
  modules: readonly AppModule[],
): OpenableModuleOption[] {
  const modBySlug = new Map(modules.map((m) => [m.slug, m]))
  const byId = new Map(instances.map((i) => [i.id, i]))

  const leaves = instances.filter((inst) => modBySlug.get(inst.slug)?.kind !== 'group')

  const baseLabels = leaves.map((inst) => instanceDisplayLabel(inst, modBySlug))
  const counts = new Map<string, number>()
  for (const label of baseLabels) {
    counts.set(label, (counts.get(label) ?? 0) + 1)
  }

  return leaves.map((inst, i) => {
    let label = baseLabels[i]!
    if ((counts.get(label) ?? 0) > 1 && inst.parentMid) {
      const parent = byId.get(inst.parentMid)
      if (parent) {
        label = `${label} (${instanceDisplayLabel(parent, modBySlug)})`
      }
    }
    return { label, instanceId: inst.id, slug: inst.slug }
  })
}

/** Stable fingerprint of the openable catalog (ids + labels, sorted). */
export function openableCatalogKey(
  instances: readonly ModuleInstance[],
  modules: readonly AppModule[],
): string {
  return buildOpenableModuleOptions(instances, modules)
    .map((o) => `${o.instanceId}\0${o.label}`)
    .sort()
    .join('\n')
}

/**
 * Fingerprint for global assistant tools: openable modules + queryable entities.
 * Rebuild tools when either catalog changes (provider tool-prefix cache).
 */
export function globalAssistantCatalogKey(
  instances: readonly ModuleInstance[],
  modules: readonly AppModule[],
): string {
  return `${openableCatalogKey(instances, modules)}\n---\n${queryableCatalogKey(instances)}`
}

function describeCurrentScreen(snap: AssistantLocationSnapshot): string {
  const { pathname, mid, modules, instances } = snap

  if (pathname === '/' || pathname === '') {
    return 'Você está na Área de trabalho do console SysColect.'
  }

  if (pathname.startsWith('/m/')) {
    const slug = pathname.slice(3).split('/')[0] ?? ''
    const def = modules.find((m) => m.slug === slug)
    const inst = mid ? instances.find((i) => i.id === mid) : undefined
    const label = inst?.label ?? def?.label ?? slug
    return `Você está no módulo "${label}" (tela ${slug}).`
  }

  return `Você está na tela ${pathname}.`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Global tools for the shell assistant (whereAmI + openModule + data search/create + memory).
 * `getSnapshot` / `navigate` must read the latest shell state at call time.
 * `openable` / `queryable` are enum catalogs — rebuild when fingerprints change.
 * `createDeps` stamps org + audit reason on silent create (request 0295).
 * `memoryDeps` posts to `/ai/v1/memory` (request 0317) — never rememberable.
 */
export function createAssistantGlobalTools(
  getSnapshot: () => AssistantLocationSnapshot,
  navigate: (to: string) => void,
  openable: readonly OpenableModuleOption[],
  queryable: readonly AssistantQueryableEntity[],
  createDeps?: AssistantCreateToolsDeps,
  memoryDeps?: AssistantMemoryToolsDeps,
): AssistantTool[] {
  const byLabel = new Map(openable.map((o) => [o.label, o]))
  const enumLabels = openable.map((o) => o.label)

  const whereAmI: AssistantTool<Record<string, never>> = {
    name: 'app.whereAmI',
    description:
      'Informa em qual módulo e tela o usuário está neste momento, em uma frase.',
    parameters: {
      type: 'object',
      properties: {},
      additionalProperties: false,
    },
    kind: 'read',
    validate: () => ({ ok: true, params: {} }),
    // Answers a question, changes nothing: confirming it is pure noise.
    // `'never'` is honoured only for `kind: 'read'` — writes always confirm.
    confirm: 'never',
    confirmSummary: () => 'Dizer em que tela você está',
    run: () => {
      const message = describeCurrentScreen(getSnapshot())
      return { ok: true, message }
    },
  }

  type OpenModuleParams = { module: string }

  const openModule: AssistantTool<OpenModuleParams> = {
    name: 'app.openModule',
    description: 'Abre um módulo do console pelo rótulo da barra lateral.',
    parameters: {
      type: 'object',
      properties: {
        module: {
          type: 'string',
          ...(enumLabels.length > 0 ? { enum: enumLabels } : {}),
          description: 'Rótulo do módulo como na barra lateral.',
        },
      },
      required: ['module'],
      additionalProperties: false,
    },
    kind: 'read',
    // Confirms (default policy). Not rememberable: navigation abandons the current
    // screen context — keep asking each time (request 0297).
    validate: (raw) => {
      if (!isRecord(raw)) return { ok: false, error: 'não entendi: parâmetros inválidos' }
      const module = raw.module
      if (typeof module !== 'string' || !module.trim()) {
        return { ok: false, error: 'não entendi: informe o módulo pelo rótulo da barra lateral' }
      }
      if (!byLabel.has(module)) {
        return { ok: false, error: `módulo desconhecido: "${module}"` }
      }
      return { ok: true, params: { module } }
    },
    confirmSummary: (p) => `Abrir ${p.module}`,
    run: (params) => {
      const option = byLabel.get(params.module)
      if (!option) {
        return { ok: false, message: `Módulo desconhecido: "${params.module}".` }
      }
      const snap = getSnapshot()
      if (snap.mid === option.instanceId) {
        return { ok: true, message: `Você já está em "${params.module}".` }
      }
      navigate(`/m/${option.slug}?mid=${option.instanceId}`)
      return {
        ok: true,
        message: `Abri "${params.module}".`,
        // 12s era curto: numa conversa real a tela de Centros de custo registrou o
        // escopo logo depois de estourar (chunk lazy frio). Sem timeoutMs vale o
        // default do núcleo (20s), que é o teto para desistir de esperar.
        awaitScope: { scopeId: `module:${option.instanceId}` },
      }
    },
  }

  const tools: AssistantTool[] = [
    whereAmI,
    openModule,
    ...createAssistantDataTools(queryable, createDeps),
  ]
  if (memoryDeps) {
    tools.push(createMemoryRememberTool(memoryDeps))
  }
  return tools
}
